import { getTelematicProviderFromURL } from "myParkdUtils/urlParameterCatching";
import { useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { RegistrationFlowType } from "myParkdUtils/enums";

/* 
    examples of website URL:
    https://parkd.com/nl/getparkd/?telematicprovider=fleetcomplete
    will be redirected to:
    https://my.parkd.com/register-trackandtrace?telematicprovider=fleetcomplete
*/

type Props = {
    flowType: RegistrationFlowType,
    updateTelematicProvider: (telematicProviderType: string) => void;
}

export const TelematicPartnerLogo = (props: Props) => {

    const [telematicPartnerMetaData, setTelematicPartnerMetaData] = useState<TelematicPartnerMetaData>({ logo: undefined, telematicProviderType: undefined });
    const [partnerName, setPartnerName] = useState<string>();

    const { flowType } = props;

    useEffect(() => {
        if (flowType === 'TRACK_AND_TRACE') {
            checkURLForTelematicProviderParam();
        }
    }, []);

    const checkURLForTelematicProviderParam = () => {
        const partner = getTelematicProviderFromURL();
        setPartnerName(partner);
        if (partner) {
            const { logo, telematicProviderType } = getTelematicPartnerMetaData(partner);
            setTelematicPartnerMetaData({ logo, telematicProviderType });
            telematicProviderType && props.updateTelematicProvider(telematicProviderType);
        }
    }

    if (telematicPartnerMetaData.logo) {
        return (
            <div className="center inline-block ml-1" data-testid="telematic-partner-logo" >
                <img id="telematic-partner-logo" src={`/telematic-partner-logos/${telematicPartnerMetaData.logo}`} alt={`logo ${partnerName}`} />
            </div>
        )
    }

    return null;
}


type TelematicPartnerMetaData = {
    logo?: string | undefined,
    telematicProviderType: string | undefined
}

// We also have reseller partners where the logo and telematicprovider can be divirgent 
// eg: On-route is a reseller of WebfleetSolutions (logo = On-route, telematicProvider = WebfleetSolutions)

// To prefill the telematic provider dropdown, the telematicProviderType in the switch below must match exactly with the providerType returned from our API.
// https://dev-api.parkd.be/telematicaccounts/all

// Always add new partners alphabetically!
// todo: review list with Olivier
export const getTelematicPartnerMetaData = (partner: string): TelematicPartnerMetaData => {
    switch (partner) {
        case 'abax':
            return { logo: 'abax.svg', telematicProviderType: 'ABAX' }
        case 'arvalleasing':
        case 'arvalconnect':
            return { logo: 'arval.png', telematicProviderType: 'ARVAL' }
        case 'ap-l':
            return { logo: 'ap-l.png', telematicProviderType: undefined }
        case 'arc':
            return { logo: 'arc-webfleet.png', telematicProviderType: undefined }
        case 'eurecamobile':
            return { logo: 'eureca-mobile.jpg', telematicProviderType: undefined }
        case 'europetrack': // deprecated?
            return { logo: 'europetrack.png', telematicProviderType: 'EUROPETRACK' }
        case 'fleetcompass':
            return { logo: 'fleetcompass.png', telematicProviderType: undefined }
        case 'fleetcomplete':
            return { logo: 'fleetcomplete.png', telematicProviderType: 'FC' }// legacy?
        case 'fleetcompleteplus':
            return { logo: 'fleetcomplete.png', telematicProviderType: 'FCPLUS' }
        case 'fleetfinder':
            return { logo: 'fleetfinder.svg', telematicProviderType: undefined }
        case 'fleetgo':
            return { logo: 'fleetgo.png', telematicProviderType: 'FLEETGO' }
        case 'fleetkennis': // not a real telematic provider
            return { logo: 'fleetkennis.svg', telematicProviderType: undefined }
        case 'fordpro':
            return { logo: 'fordpro.png', telematicProviderType: undefined }
        case 'free2move':
            return { logo: 'free2move.svg', telematicProviderType: undefined }
        case 'geodynamics':
            return { logo: 'geodynamics.png', telematicProviderType: undefined }
        case 'geotab':
            return { logo: 'geotab.png', telematicProviderType: 'GEOTAB' }
        case 'geotrack':
            return { logo: 'geotrack.png', telematicProviderType: 'GEOTRACK' }
        case 'gps-buddy':
            return { logo: 'gps-buddy.svg', telematicProviderType: 'GPSBUDDY' }
        case 'gpsfleetconsultancybv':
            return { logo: 'gpsfleetconsultancybv.png', telematicProviderType: undefined }
        case 'hanabconnectivitysolutions':
            return { logo: 'hanab-connectivity-solutions.svg', telematicProviderType: undefined }
        case 'hyrde':
            return { logo: 'hyrde.svg', telematicProviderType: undefined }
        case 'linqo':
            return { logo: 'linqo.svg', telematicProviderType: 'LINQO' }
        case 'masternaut':
            return { logo: 'masternaut.png', telematicProviderType: 'MASTERNAUT' }
        case 'mergroup':
            return { logo: 'mergroup.png', telematicProviderType: 'MERGROUP' }
        case 'myfms':
            return { logo: 'myfms.png', telematicProviderType: 'MYFMS' }
        case 'michelinconnectedfleet':
            return { logo: 'michelin-connected-fleet.png', telematicProviderType: undefined }
        case 'movingintelligence':
            return { logo: 'moving-intelligence.png', telematicProviderType: undefined }
        case 'nedtrack':
            return { logo: 'nedtrack.png', telematicProviderType: 'NEDTRACK' }
        case 'on-route':
            return { logo: 'on-route-black.svg', telematicProviderType: 'FC' } // FCPLUS ?
        case 'ram':
            return { logo: 'ram-track-and-trace.png', telematicProviderType: 'RAM' }
        case 'rauwers':
            return { logo: 'rauwers.svg', telematicProviderType: undefined }
        case 'routeconnect':
            return { logo: 'routeconnect.png', telematicProviderType: undefined }
        case 'routevision':
            return { logo: 'routevision.svg', telematicProviderType: 'ROUTEVISION' }
        case 'slimmgps': // deprecated (moving intelligence alias)
            return { logo: 'slimmgps.png', telematicProviderType: undefined }
        case 'suivo':
            return { logo: 'suivo.png', telematicProviderType: 'SUIVO' }
        case 'tmc':
            return { logo: 'tmc.png', telematicProviderType: undefined }
        case 'transscope':
            return { logo: 'transscope.png', telematicProviderType: undefined }
        case 'verizonconnect':
            return { logo: 'verizon-connect.svg', telematicProviderType: undefined }
        case 'viasat':
        case 'viasatconnect':
        case 'viasatbelgium':
        case 'securysat': // Viasat alias
            return { logo: 'viasat-connect.png', telematicProviderType: 'VIASAT' }
        case 'webfleet':
        case 'webfleetsolutions':
            return { logo: 'bridgestone-webfleet.svg', telematicProviderType: 'TT' }
        case 'wego':
            return { logo: 'wego.png', telematicProviderType: undefined }

        default:
            console.error(`Telematic partner logo missing for partner: ${partner}`);
            Sentry.captureMessage(`Telematic partner metadata is missing for provider: ${partner}`);
            return { logo: undefined, telematicProviderType: undefined }
    }
}
